/**
 * Groups per-return bulk outcomes into panel sections (marked done / manual actions / errors / not processed).
 */
import {
    BULK_MARK_DONE_REASON_CODE,
    BULK_STATUS_PANEL_BUCKET,
    getBulkReasonPanelBucket,
    IBulkNewReport,
    IBulkRequestReturnOutcome,
    reasonTranslationKey,
} from "../../../../services/api/bulk-rma-action-types";

export interface IBulkStatusPanelSummary {
    succeeded: number;
    total: number;
    needReview: number;
}

export interface IBulkStatusPanelRow {
    rowKey: string;
    count: number;
    labelKey: string;
    labelText?: string;
    rmaIds: number[];
}

export interface IBulkStatusPanelSection {
    bucket: BULK_STATUS_PANEL_BUCKET;
    titleKey: string;
    rows: IBulkStatusPanelRow[];
    totalCount: number;
}

const SECTION_TITLE_KEY: Record<BULK_STATUS_PANEL_BUCKET, string> = {
    [BULK_STATUS_PANEL_BUCKET.MARKED_AS_DONE]: "BULK_STATUS_MARKED_AS_DONE",
    [BULK_STATUS_PANEL_BUCKET.STILL_OPEN]: "MANUAL_ACTIONS",
    [BULK_STATUS_PANEL_BUCKET.ERROR]: "ERRORS",
    [BULK_STATUS_PANEL_BUCKET.NOT_PROCESSED]: "BULK_STATUS_NOT_PROCESSED",
};

const SECTION_ORDER: readonly BULK_STATUS_PANEL_BUCKET[] = [
    BULK_STATUS_PANEL_BUCKET.MARKED_AS_DONE,
    BULK_STATUS_PANEL_BUCKET.STILL_OPEN,
    BULK_STATUS_PANEL_BUCKET.ERROR,
    BULK_STATUS_PANEL_BUCKET.NOT_PROCESSED,
];

const SUCCEEDED_STATUS = "succeeded";

interface IRowAccumulator {
    rowKey: string;
    labelKey: string;
    labelText?: string;
    bucket: BULK_STATUS_PANEL_BUCKET;
    rmaIds: number[];
}

function rowKeyForOutcome(outcome: IBulkRequestReturnOutcome): string {
    if (outcome.status === SUCCEEDED_STATUS) {
        return SUCCEEDED_STATUS;
    }
    const reasonCode = outcome.reason_code ?? "unknown";
    if (reasonCode === BULK_MARK_DONE_REASON_CODE.EXECUTION_ERROR) {
        const message = outcome.reason_message?.trim() ?? "";
        return `${reasonCode}:${message}`;
    }
    return reasonCode;
}

function rowSortKey(row: IBulkStatusPanelRow): string {
    return row.labelText ?? row.labelKey;
}

function emptyRowsByBucket(): Record<
    BULK_STATUS_PANEL_BUCKET,
    IBulkStatusPanelRow[]
> {
    return {
        [BULK_STATUS_PANEL_BUCKET.MARKED_AS_DONE]: [],
        [BULK_STATUS_PANEL_BUCKET.STILL_OPEN]: [],
        [BULK_STATUS_PANEL_BUCKET.ERROR]: [],
        [BULK_STATUS_PANEL_BUCKET.NOT_PROCESSED]: [],
    };
}

function buildRowsByBucket(
    returns: IBulkRequestReturnOutcome[]
): Record<BULK_STATUS_PANEL_BUCKET, IBulkStatusPanelRow[]> {
    const byRowKey = new Map<string, IRowAccumulator>();

    for (const outcome of returns) {
        const rowKey = rowKeyForOutcome(outcome);
        const existing = byRowKey.get(rowKey);
        if (existing) {
            existing.rmaIds.push(outcome.rma_id);
            continue;
        }

        if (outcome.status === SUCCEEDED_STATUS) {
            byRowKey.set(rowKey, {
                rowKey,
                labelKey: "BULK_STATUS_ALL_COMPLETED_RMAS",
                bucket: BULK_STATUS_PANEL_BUCKET.MARKED_AS_DONE,
                rmaIds: [outcome.rma_id],
            });
            continue;
        }

        const reasonCode = outcome.reason_code ?? "unknown";
        const message = outcome.reason_message?.trim() ?? "";
        const isExecutionError =
            reasonCode === BULK_MARK_DONE_REASON_CODE.EXECUTION_ERROR;

        byRowKey.set(rowKey, {
            rowKey,
            labelKey: isExecutionError
                ? reasonTranslationKey(
                      BULK_MARK_DONE_REASON_CODE.EXECUTION_ERROR
                  )
                : reasonTranslationKey(reasonCode),
            ...(isExecutionError && message ? { labelText: message } : {}),
            bucket: getBulkReasonPanelBucket(reasonCode),
            rmaIds: [outcome.rma_id],
        });
    }

    const rowsByBucket = emptyRowsByBucket();
    for (const group of byRowKey.values()) {
        rowsByBucket[group.bucket].push({
            rowKey: group.rowKey,
            count: group.rmaIds.length,
            labelKey: group.labelKey,
            ...(group.labelText ? { labelText: group.labelText } : {}),
            rmaIds: group.rmaIds,
        });
    }

    return rowsByBucket;
}

export class BulkStatusPanelStore {
    private readonly _succeeded: number;
    private readonly _total: number;
    readonly sections: IBulkStatusPanelSection[];

    constructor(report: IBulkNewReport, returns: IBulkRequestReturnOutcome[]) {
        this._total = report.counts.total;
        this._succeeded = report.counts.succeeded;

        const rowsByBucket = buildRowsByBucket(returns);

        for (const bucket of SECTION_ORDER) {
            rowsByBucket[bucket].sort((a, b) =>
                rowSortKey(a).localeCompare(rowSortKey(b))
            );
        }

        const sections: IBulkStatusPanelSection[] = [];
        for (const bucket of SECTION_ORDER) {
            const bucketRows = rowsByBucket[bucket];
            if (bucketRows.length === 0) {
                continue;
            }
            sections.push({
                bucket,
                titleKey: SECTION_TITLE_KEY[bucket],
                rows: bucketRows,
                totalCount: bucketRows.reduce((sum, row) => sum + row.count, 0),
            });
        }
        this.sections = sections;
    }

    get summary(): IBulkStatusPanelSummary {
        return {
            succeeded: this._succeeded,
            total: this._total,
            needReview: this._total - this._succeeded,
        };
    }
}
